import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./DepositoTrilhosPage.css";
import DepositoLoteRow from "./components/DepositoLoteRow";
import {
  RT,
  RT_META,
  connectRealtime,
  joinRoom,
  leaveRoom,
  makeEstaleiroDepositoRoom,
  off,
  on,
  onRealtimeMeta,
} from "../../../api/realtime";
import { listarBarrasDeposito, listarLotesDeposito, obterLoteDeposito, atualizarDocumentosLoteDeposito } from "../estaleiro.api";
import { listarPerfisTrilhoEstaleiro } from "../../bases-estaleiro/perfil-trilhos/perfilTrilhosEstaleiro.api";
import { listarClassesEstaleiro } from "../../bases-estaleiro/classes/classesEstaleiro.api";

const TIPO_LOTE_OPTIONS = [
  { value: "TRILHOS_NOVOS", label: "Novo" },
  { value: "TRILHOS_REEMPREGO", label: "Reemprego" },
];
const TIPO_LOTE_FILTER_OPTIONS = [{ value: "", label: "Todos" }, ...TIPO_LOTE_OPTIONS];
const DEFAULT_LIMIT = 100;

const DEFAULT_FILTERS = {
  q: "",
  tipo_lote: "",
  origem_fluxo: "",
  perfil_trilho: "",
  classe_trilho: "",
  data_de: "",
  data_ate: "",
  limit: DEFAULT_LIMIT,
  offset: 0,
};

const LOTE_COLUMNS = [
  { key: "data", label: "Data", colClass: "dep-lote-col--date" },
  { key: "codigo", label: "Codigo do Lote", colClass: "dep-lote-col--codigo" },
  { key: "origem", label: "Origem", colClass: "dep-lote-col--origem" },
  { key: "perfil", label: "Perfil", colClass: "dep-lote-col--perfil" },
  { key: "nota", label: "Nota Fiscal", colClass: "dep-lote-col--nota" },
  { key: "pedido", label: "Ped. Transf.", colClass: "dep-lote-col--pedido" },
  { key: "receb", label: "Receb. Geracao", colClass: "dep-lote-col--receb" },
  { key: "carreta", label: "Carreta", colClass: "dep-lote-col--carreta" },
  { key: "cavalo", label: "Cavalo", colClass: "dep-lote-col--cavalo" },
  { key: "barras", label: "Total Barras", colClass: "dep-lote-col--barras" },
  { key: "toneladas", label: "Total Toneladas", colClass: "dep-lote-col--toneladas" },
  { key: "metragem", label: "Total Metragem", colClass: "dep-lote-col--metragem" },
];

const toNumber = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const raw = String(value ?? "").trim().replace(/\s+/g, "");
  if (!raw) return 0;
  const normalized = raw.includes(",") ? raw.replace(",", ".") : raw;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatInt = (value) =>
  new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(toNumber(value));

const formatNumber = (value, digits = 2) =>
  new Intl.NumberFormat("pt-BR", { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(toNumber(value));

const formatDate = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [year, month, day] = raw.split("-");
    return `${day}/${month}/${year}`;
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return new Intl.DateTimeFormat("pt-BR").format(parsed);
};

const splitDocumentos = (value) =>
  Array.from(
    new Set(
      String(value ?? "")
        .split(/[|,;\n\r]+/g)
        .map((item) => item.trim())
        .filter(Boolean)
    )
  );

const joinDocumentos = (items = []) => splitDocumentos(items).join(" | ");

const normalizeFilters = (base = {}) => ({
  q: String(base.q || "").trim(),
  tipo_lote: String(base.tipo_lote || "").trim(),
  origem_fluxo: String(base.origem_fluxo || "").trim(),
  perfil_trilho: String(base.perfil_trilho || "").trim(),
  classe_trilho: String(base.classe_trilho || "").trim(),
  data_de: String(base.data_de || "").trim(),
  data_ate: String(base.data_ate || "").trim(),
  limit: Math.max(20, Math.min(500, Number(base.limit) || DEFAULT_LIMIT)),
  offset: 0,
});

const normalizePerfil = (value) => String(value || "").trim();
const normalizeClasse = (value) => String(value || "").trim();
const normalizeSearch = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const splitPerfisLote = (value) =>
  Array.from(
    new Set(
      String(value || "")
        .split("|")
        .map((item) => normalizePerfil(item))
        .filter(Boolean)
    )
  );

const toDateKey = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const match = raw.match(/^(\d{4}-\d{2}-\d{2})[ T]/);
  if (match) return match[1];
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
};

const includesQuery = (pieces, query) => {
  const q = normalizeSearch(query);
  if (!q) return true;
  return normalizeSearch(pieces.filter(Boolean).join(" ")).includes(q);
};

const sortLotesRows = (rows = []) =>
  [...rows].sort(
    (a, b) =>
      Number(b?.id || 0) - Number(a?.id || 0) ||
      String(b?.created_at || "").localeCompare(String(a?.created_at || ""))
  );

const sortSummaryRows = (rows = []) =>
  [...rows].sort(
    (a, b) =>
      Number(b?.id || 0) - Number(a?.id || 0) ||
      String(b?.created_at || "").localeCompare(String(a?.created_at || ""))
  );

const normalizeRealtimeLote = (lote) => {
  if (!lote || typeof lote !== "object") return null;
  const codigoLote = String(lote.codigo_lote || "").trim();
  if (!codigoLote) return null;
  return { ...lote, codigo_lote: codigoLote };
};

const normalizeRealtimeBarra = (barra, lote = null) => {
  if (!barra || typeof barra !== "object") return null;
  const fallback = lote && typeof lote === "object" ? lote : {};
  const codigoLote = String(barra.codigo_lote ?? fallback.codigo_lote ?? "").trim();
  if (!codigoLote) return null;
  return {
    ...fallback,
    ...barra,
    codigo_lote: codigoLote,
    codigo_barra: String(barra.codigo_barra || "").trim(),
    tipo_lote: barra.tipo_lote ?? fallback.tipo_lote ?? "",
    origem_fluxo: barra.origem_fluxo ?? fallback.origem_fluxo ?? "",
    data_referencia: barra.data_referencia ?? fallback.data_referencia ?? "",
  };
};

const loteMatchesFilters = (lote, filters = {}) => {
  if (!lote) return false;
  if (filters.tipo_lote && String(lote.tipo_lote || "").trim() !== filters.tipo_lote) return false;
  if (filters.origem_fluxo && String(lote.origem_fluxo || "").trim() !== filters.origem_fluxo) return false;
  if (filters.perfil_trilho) {
    const perfilSelecionado = normalizeSearch(filters.perfil_trilho);
    const perfisLote = splitPerfisLote(lote.perfil_trilho);
    if (!perfisLote.some((perfil) => normalizeSearch(perfil) === perfilSelecionado)) return false;
  }

  const day = toDateKey(lote.data_referencia || lote.created_at);
  if (filters.data_de && (!day || day < filters.data_de)) return false;
  if (filters.data_ate && (!day || day > filters.data_ate)) return false;

  return includesQuery(
    [
      lote.codigo_lote,
      lote.origem,
      lote.perfil_trilho,
      lote.marca_barra,
      lote.nota_fiscal,
      lote.pedido_transferencia,
    ],
    filters.q
  );
};

const barraMatchesFilters = (barra, filters = {}, lote = null) => {
  if (!barra) return false;
  const ref = lote ? { ...lote, ...barra } : barra;
  if (filters.tipo_lote && String(ref.tipo_lote || "").trim() !== filters.tipo_lote) return false;
  if (filters.origem_fluxo && String(ref.origem_fluxo || "").trim() !== filters.origem_fluxo) return false;
  if (filters.perfil_trilho && normalizePerfil(ref.perfil_trilho) !== normalizePerfil(filters.perfil_trilho)) return false;
  if (filters.classe_trilho && normalizeClasse(ref.classe_trilho) !== normalizeClasse(filters.classe_trilho)) return false;

  const day = toDateKey(ref.data_referencia || ref.created_at);
  if (filters.data_de && (!day || day < filters.data_de)) return false;
  if (filters.data_ate && (!day || day > filters.data_ate)) return false;

  return includesQuery(
    [
      ref.codigo_barra,
      ref.codigo_lote,
      ref.codigo_vagao,
      ref.perfil_trilho,
      ref.classe_trilho,
      ref.origem,
      ref.marca_barra,
      ref.nota_fiscal,
      ref.pedido_transferencia,
      ref.motivo_defeito,
    ],
    filters.q
  );
};

const classifyClasseGroup = (classeValue) => {
  const classe = String(classeValue || "").trim().toLowerCase();
  if (classe.includes("novo")) return "NOVO";
  if (classe.includes("sucata")) return "SUCATA";
  return "REEMPREGO";
};

const extractPerfilNumero = (perfilValue) => {
  const text = String(perfilValue || "").replace(",", ".").toUpperCase();
  const match = text.match(/(\d+(?:\.\d+)?)/);
  return match ? toNumber(match[1]) : 0;
};

const calcToneladas = (metros, perfilValue) => (toNumber(metros) * extractPerfilNumero(perfilValue)) / 1000;

const calcToneladasLoteFallback = (lote) => {
  const perfis = splitPerfisLote(lote?.perfil_trilho);
  if (perfis.length !== 1) return 0;
  return calcToneladas(lote?.total_metragem, perfis[0]);
};

const DATE_WEEKDAYS = ["D", "S", "T", "Q", "Q", "S", "S"];

const startOfDayDate = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const startOfMonthDate = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const addDaysDate = (date, amount) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + amount);
const addMonthsDate = (date, amount) => new Date(date.getFullYear(), date.getMonth() + amount, 1);
const isSameDayDate = (a, b) =>
  !!a &&
  !!b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();
const isSameMonthDate = (a, b) => !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();

const parseYmdDate = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const next = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(next.getTime()) ? null : next;
};

const formatYmdDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatMonthTitleDate = (date) =>
  date.toLocaleDateString("pt-BR", { month: "long", year: "numeric" }).replace(/^./, (char) => char.toUpperCase());

const normalizeSelectOptions = (options = []) =>
  options
    .map((option) => {
      if (typeof option === "string") return { value: option, label: option };
      return {
        value: option?.value ?? "",
        label: option?.label ?? String(option?.value ?? ""),
      };
    })
    .filter((option) => option.value !== undefined && option.value !== null);

function DepDateField({
  value,
  onChange,
  placeholder = "Selecione",
  ariaLabel = "Selecionar data",
  panelAlign = "left",
}) {
  const rootRef = useRef(null);
  const today = useMemo(() => startOfDayDate(new Date()), []);
  const selectedDate = useMemo(() => parseYmdDate(value), [value]);
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => startOfMonthDate(selectedDate || today));

  useEffect(() => {
    if (open) return;
    const nextMonth = startOfMonthDate(selectedDate || today);
    setViewMonth((prev) => (isSameMonthDate(prev, nextMonth) ? prev : nextMonth));
  }, [open, selectedDate, today]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const monthStart = startOfMonthDate(viewMonth);
  const gridStart = addDaysDate(monthStart, -monthStart.getDay());
  const calendarDays = useMemo(
    () => Array.from({ length: 42 }, (_, index) => addDaysDate(gridStart, index)),
    [gridStart]
  );

  const handlePick = useCallback(
    (day) => {
      onChange(formatYmdDate(day));
      setOpen(false);
    },
    [onChange]
  );

  return (
    <div className={`dep-dateRoot dep-dateRoot--${panelAlign}${open ? " is-open" : ""}`} ref={rootRef}>
      <button
        type="button"
        className={`dep-dateBtn ${open ? "open" : ""}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className={`dep-dateValue ${selectedDate ? "" : "is-placeholder"}`}>
          {selectedDate ? formatDate(formatYmdDate(selectedDate)) : placeholder}
        </span>
      </button>

      {open && (
        <div className="dep-datePanel" role="dialog" aria-label={ariaLabel}>
          <div className="dep-datePanelHead">
            <button
              type="button"
              className="dep-dateNav"
              onClick={() => setViewMonth((prev) => addMonthsDate(prev, -1))}
              aria-label="Mes anterior"
            >
              {"<"}
            </button>
            <div className="dep-datePanelTitle">{formatMonthTitleDate(viewMonth)}</div>
            <button
              type="button"
              className="dep-dateNav"
              onClick={() => setViewMonth((prev) => addMonthsDate(prev, 1))}
              aria-label="Proximo mes"
            >
              {">"}
            </button>
          </div>

          <div className="dep-dateWeekdays">
            {DATE_WEEKDAYS.map((weekday, index) => (
              <span key={`${weekday}-${index}`} className="dep-dateWeekday">
                {weekday}
              </span>
            ))}
          </div>

          <div className="dep-dateGrid">
            {calendarDays.map((day) => {
              const isOutside = !isSameMonthDate(day, viewMonth);
              const isSelected = isSameDayDate(day, selectedDate);
              const isToday = isSameDayDate(day, today);

              return (
                <button
                  type="button"
                  key={formatYmdDate(day)}
                  className={[
                    "dep-dateDay",
                    isOutside ? "is-outside" : "",
                    isSelected ? "is-selected" : "",
                    isToday ? "is-today" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => handlePick(day)}
                  aria-pressed={isSelected}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>

          <div className="dep-datePanelFoot">
            <button type="button" className="dep-dateAction" onClick={() => handlePick(today)}>
              Hoje
            </button>
            <button type="button" className="dep-dateAction dep-dateActionGhost" onClick={() => setOpen(false)}>
              Fechar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function DepSmoothSelect({
  value,
  onChange,
  options,
  placeholder = "Selecione",
  title = "Selecione",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const normalizedOptions = useMemo(() => normalizeSelectOptions(options), [options]);
  const selected = normalizedOptions.find((option) => String(option.value) === String(value ?? ""));

  useEffect(() => {
    if (!open) return undefined;

    const onClickOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onEsc = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const handlePick = (nextValue) => {
    onChange(nextValue);
    setOpen(false);
  };

  return (
    <div className="dep-msRoot" ref={rootRef}>
      <button
        type="button"
        className={`dep-msBtn ${open ? "open" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          if (!disabled) setOpen((prev) => !prev);
        }}
        disabled={disabled}
      >
        <span className="dep-msBtnText">
          <span className={`dep-msText ${selected ? "" : "is-placeholder"}`}>
            {selected?.label || placeholder}
          </span>
          <span className="dep-msMeta">{selected ? "1 selecionado" : "Nenhum selecionado"}</span>
        </span>
      </button>

      {open && (
        <div className="dep-msPanel" role="listbox">
          <div className="dep-msHeader">
            <div className="dep-msHeaderCopy">
              <span className="dep-msHeaderTitle">{title}</span>
              <span className="dep-msHeaderMeta">Escolha uma opcao</span>
            </div>
            <button type="button" className="dep-msActionBtn" onClick={() => handlePick("")}>Limpar</button>
          </div>

          <div className="dep-msListWrap">
            <div className="dep-msList">
              {normalizedOptions.map((option) => {
                const isSelected = String(option.value) === String(value ?? "");
                return (
                  <button
                    type="button"
                    key={`${option.value}-${option.label}`}
                    className={`dep-msItem ${isSelected ? "is-selected" : ""}`}
                    onClick={() => handlePick(option.value)}
                    aria-pressed={isSelected}
                  >
                    <span className="dep-msLabel">{option.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DepSearchableSelect({
  value,
  onChange,
  options,
  placeholder = "Digite para buscar",
  title = "Selecione",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const normalizedOptions = useMemo(() => normalizeSelectOptions(options), [options]);
  const selected = normalizedOptions.find((option) => String(option.value) === String(value ?? ""));

  useEffect(() => {
    if (!open) return undefined;

    const onClickOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onEsc = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      return;
    }
    const id = window.requestAnimationFrame(() => searchRef.current?.focus());
    return () => window.cancelAnimationFrame(id);
  }, [open]);

  const filteredOptions = useMemo(() => {
    const normalizedQuery = normalizeSearch(query);
    if (!normalizedQuery) return normalizedOptions;
    return normalizedOptions.filter((option) => normalizeSearch(option.label).includes(normalizedQuery));
  }, [normalizedOptions, query]);

  const handlePick = (nextValue) => {
    onChange(nextValue);
    setOpen(false);
  };

  return (
    <div className="dep-msRoot" ref={rootRef}>
      <button
        type="button"
        className={`dep-msBtn ${open ? "open" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          if (!disabled) setOpen((prev) => !prev);
        }}
        disabled={disabled}
      >
        <span className="dep-msBtnText">
          <span className={`dep-msText ${selected ? "" : "is-placeholder"}`}>
            {selected?.label || placeholder}
          </span>
          <span className="dep-msMeta">{selected ? selected.label : "Nenhum perfil selecionado"}</span>
        </span>
      </button>

      {open && (
        <div className="dep-msPanel" role="listbox">
          <div className="dep-msHeader">
            <div className="dep-msHeaderCopy">
              <span className="dep-msHeaderTitle">{title}</span>
              <span className="dep-msHeaderMeta">Busque e selecione um perfil</span>
            </div>
            <button type="button" className="dep-msActionBtn" onClick={() => handlePick("")}>Limpar</button>
          </div>

          <div className="dep-msSearchWrap">
            <input
              ref={searchRef}
              type="text"
              className="dep-msSearch"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Buscar perfil"
            />
          </div>

          <div className="dep-msListWrap">
            <div className="dep-msList">
              {filteredOptions.length ? (
                filteredOptions.map((option) => {
                  const isSelected = String(option.value) === String(value ?? "");
                  return (
                    <button
                      type="button"
                      key={`${option.value}-${option.label}`}
                      className={`dep-msItem ${isSelected ? "is-selected" : ""}`}
                      onClick={() => handlePick(option.value)}
                      aria-pressed={isSelected}
                    >
                      <span className="dep-msLabel">{option.label}</span>
                    </button>
                  );
                })
              ) : (
                <div className="dep-msEmpty">Nenhum perfil encontrado.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LoteColGroup() {
  return (
    <colgroup>
      {LOTE_COLUMNS.map((column) => (
        <col key={column.key} className={column.colClass} />
      ))}
    </colgroup>
  );
}

export default function DepositoTrilhosPage({ onBack }) {
  const [summaryRows, setSummaryRows] = useState([]);
  const [lotesRows, setLotesRows] = useState([]);
  const [loteDetailsByCodigo, setLoteDetailsByCodigo] = useState({});
  const [expandedLotes, setExpandedLotes] = useState({});
  const [editDocsByLote, setEditDocsByLote] = useState({});

  const [perfilCatalogo, setPerfilCatalogo] = useState([]);
  const [classeCatalogo, setClasseCatalogo] = useState([]);
  const [draftFilters, setDraftFilters] = useState(DEFAULT_FILTERS);
  const [activeFilters, setActiveFilters] = useState(DEFAULT_FILTERS);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState("");

  const nextOffsetRef = useRef(0);
  const lotesScrollRef = useRef(null);
  const lotesHeadScrollRef = useRef(null);
  const activeFiltersRef = useRef(DEFAULT_FILTERS);
  const expandedLotesRef = useRef({});
  const loteDetailsRef = useRef({});
  const resumoReloadTimerRef = useRef(null);
  const pendingRowClickRef = useRef({ timer: null, code: "" });
  useEffect(() => {
    activeFiltersRef.current = activeFilters;
  }, [activeFilters]);

  useEffect(() => {
    expandedLotesRef.current = expandedLotes;
  }, [expandedLotes]);

  useEffect(() => {
    loteDetailsRef.current = loteDetailsByCodigo;
  }, [loteDetailsByCodigo]);

  useEffect(
    () => () => {
      if (pendingRowClickRef.current?.timer) {
        clearTimeout(pendingRowClickRef.current.timer);
      }
      if (resumoReloadTimerRef.current) {
        clearTimeout(resumoReloadTimerRef.current);
      }
    },
    []
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [perfis, classes] = await Promise.all([
          listarPerfisTrilhoEstaleiro({ ativo: 1, limit: 1000, offset: 0 }),
          listarClassesEstaleiro({ ativo: 1, limit: 1000, offset: 0 }),
        ]);
        if (cancelled) return;
        setPerfilCatalogo(
          Array.from(new Set((perfis || []).map((p) => normalizePerfil(p?.perfil_trilho)).filter(Boolean))).sort((a, b) =>
            a.localeCompare(b, "pt-BR", { numeric: true })
          )
        );
        setClasseCatalogo(
          Array.from(
            new Set(
              (classes || [])
                .map((c) => normalizeClasse(c?.classe_sub_classe || (c?.sub_classe ? `${c?.classe} - ${c?.sub_classe}` : c?.classe)))
                .filter(Boolean)
            )
          ).sort((a, b) => a.localeCompare(b, "pt-BR", { numeric: true }))
        );
      } catch {}
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const carregarResumo = useCallback(async (safeFilters) => {
    const summary = await listarBarrasDeposito({ ...safeFilters, limit: 5000, offset: 0 });
    setSummaryRows(Array.isArray(summary) ? summary : []);
  }, []);

  const carregarLotes = useCallback(async (safeFilters, { append = false } = {}) => {
    const offset = append ? nextOffsetRef.current : 0;
    const chunk = await listarLotesDeposito({ ...safeFilters, limit: safeFilters.limit, offset });
    const safeChunk = Array.isArray(chunk) ? chunk : [];

    setLotesRows((prev) => {
      if (!append) return safeChunk;
      const map = new Map((prev || []).map((row) => [String(row.codigo_lote || row.id), row]));
      safeChunk.forEach((row) => map.set(String(row.codigo_lote || row.id), row));
      return Array.from(map.values());
    });

    const next = offset + safeChunk.length;
    nextOffsetRef.current = next;
    setHasMore(safeChunk.length >= safeFilters.limit);
  }, []);

  const carregarTudo = useCallback(async (baseFilters) => {
    const safeFilters = normalizeFilters(baseFilters);
    setIsLoading(true);
    setError("");
    setExpandedLotes({});
    setLoteDetailsByCodigo({});
    setEditDocsByLote({});
    nextOffsetRef.current = 0;
    setHasMore(true);

    try {
      await Promise.all([carregarResumo(safeFilters), carregarLotes(safeFilters, { append: false })]);
    } catch (err) {
      setError(String(err?.message || "Erro ao carregar dados do deposito."));
    } finally {
      setIsLoading(false);
    }
  }, [carregarLotes, carregarResumo]);

  const expandedLoteCode = useMemo(
    () => Object.keys(expandedLotes || {}).find((code) => expandedLotes[code]) || "",
    [expandedLotes]
  );

  const toneladasPorLote = useMemo(() => {
    const map = new Map();
    summaryRows.forEach((row) => {
      const codigoLote = String(row?.codigo_lote || "").trim();
      if (!codigoLote) return;
      const atual = Number(map.get(codigoLote) || 0);
      map.set(codigoLote, atual + calcToneladas(row?.metragem_pecas, row?.perfil_trilho));
    });
    return map;
  }, [summaryRows]);

  const visibleLotesRows = useMemo(() => {
    const source = !expandedLoteCode
      ? lotesRows
      : (lotesRows || []).filter((row) => String(row?.codigo_lote || "").trim() === expandedLoteCode);

    return (source || []).map((row) => {
      const codigoLote = String(row?.codigo_lote || "").trim();
      const toneladasResumo = toneladasPorLote.get(codigoLote);
      return {
        ...row,
        total_toneladas:
          typeof toneladasResumo === "number" && Number.isFinite(toneladasResumo)
            ? toneladasResumo
            : calcToneladasLoteFallback(row),
      };
    });
  }, [expandedLoteCode, lotesRows, toneladasPorLote]);

  useEffect(() => {
    const initial = normalizeFilters(DEFAULT_FILTERS);
    setDraftFilters(initial);
    setActiveFilters(initial);
    carregarTudo(initial);
  }, [carregarTudo]);

  const scheduleResumoReload = useCallback(() => {
    if (resumoReloadTimerRef.current) {
      clearTimeout(resumoReloadTimerRef.current);
    }

    resumoReloadTimerRef.current = setTimeout(async () => {
      resumoReloadTimerRef.current = null;
      try {
        await carregarResumo(normalizeFilters(activeFiltersRef.current));
      } catch (err) {
        setError(String(err?.message || "Erro ao atualizar o resumo do deposito."));
      }
    }, 140);
  }, [carregarResumo]);

  const upsertRealtimeLote = useCallback((incomingLote) => {
    const lote = normalizeRealtimeLote(incomingLote);
    if (!lote) return;

    setLotesRows((prev) => {
      const without = (prev || []).filter((row) => String(row?.codigo_lote || "").trim() !== lote.codigo_lote);
      if (!loteMatchesFilters(lote, activeFiltersRef.current)) {
        return without;
      }
      return sortLotesRows([lote, ...without]);
    });
  }, []);

  const removeRealtimeLote = useCallback((codigoLote) => {
    const code = String(codigoLote || "").trim();
    if (!code) return;

    setLotesRows((prev) => (prev || []).filter((row) => String(row?.codigo_lote || "").trim() !== code));
    setSummaryRows((prev) => (prev || []).filter((row) => String(row?.codigo_lote || "").trim() !== code));
    setExpandedLotes((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, code)) return prev;
      const next = { ...prev };
      delete next[code];
      return next;
    });
    setLoteDetailsByCodigo((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, code)) return prev;
      const next = { ...prev };
      delete next[code];
      return next;
    });
    setEditDocsByLote((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, code)) return prev;
      const next = { ...prev };
      delete next[code];
      return next;
    });
  }, []);

  const patchRealtimeBarras = useCallback((codigoLote, incomingBarras, incomingLote = null) => {
    const code = String(codigoLote || "").trim();
    if (!code) return;

    const allBarras = (Array.isArray(incomingBarras) ? incomingBarras : [])
      .map((barra) => normalizeRealtimeBarra(barra, incomingLote))
      .filter(Boolean);

    setSummaryRows((prev) => {
      const base = (prev || []).filter((row) => String(row?.codigo_lote || "").trim() !== code);
      const matches = allBarras.filter((barra) => barraMatchesFilters(barra, activeFiltersRef.current, incomingLote));
      return sortSummaryRows([...base, ...matches]).slice(0, 5000);
    });

    if (expandedLotesRef.current?.[code] || loteDetailsRef.current?.[code]?.loaded) {
      setLoteDetailsByCodigo((prev) => ({
        ...prev,
        [code]: {
          loading: false,
          loaded: true,
          error: "",
          barras: allBarras,
        },
      }));
    }
  }, []);

  const applyDepositoRealtimePayload = useCallback(
    (payload = {}) => {
      const lote = normalizeRealtimeLote(payload?.lote);
      const code = String(payload?.codigo_lote || lote?.codigo_lote || "").trim();
      const action = String(payload?.action || "UPDATE").toUpperCase();
      const hasBarrasSnapshot =
        payload?.has_barras_snapshot === true ||
        (Array.isArray(payload?.barras) && payload.barras.length > 0);

      if (!code) return;

      if (action === "DELETE") {
        removeRealtimeLote(code);
        return;
      }

      if (lote) {
        upsertRealtimeLote(lote);
      }

      if (hasBarrasSnapshot) {
        patchRealtimeBarras(code, payload?.barras || [], lote);
      } else {
        scheduleResumoReload();
      }
    },
    [patchRealtimeBarras, removeRealtimeLote, scheduleResumoReload, upsertRealtimeLote]
  );

  useEffect(() => {
    const room = makeEstaleiroDepositoRoom();
    const handleDepositoChanged = (payload) => applyDepositoRealtimePayload(payload);
    const handleReconnect = () => {
      const safeFilters = normalizeFilters(activeFiltersRef.current);
      carregarTudo(safeFilters);
    };

    connectRealtime().catch(() => {});
    joinRoom(room);
    on(RT.ESTALEIRO_DEPOSITO_CHANGED, handleDepositoChanged);
    const offReconnect = onRealtimeMeta(RT_META.RECONNECTED, handleReconnect);

    return () => {
      leaveRoom(room);
      off(RT.ESTALEIRO_DEPOSITO_CHANGED, handleDepositoChanged);
      offReconnect();
    };
  }, [applyDepositoRealtimePayload, carregarTudo]);

  const optionsFromRows = useMemo(() => {
    const perfis = Array.from(new Set(summaryRows.map((row) => normalizePerfil(row.perfil_trilho)).filter(Boolean))).sort((a, b) =>
      a.localeCompare(b, "pt-BR", { numeric: true })
    );
    const classes = Array.from(new Set(summaryRows.map((row) => normalizeClasse(row.classe_trilho)).filter(Boolean))).sort((a, b) =>
      a.localeCompare(b, "pt-BR", { numeric: true })
    );
    return { perfis, classes };
  }, [summaryRows]);

  const perfisResumo = useMemo(() => (perfilCatalogo.length ? perfilCatalogo : optionsFromRows.perfis), [perfilCatalogo, optionsFromRows.perfis]);

  const classesReempregoResumo = useMemo(() => {
    const source = classeCatalogo.length ? classeCatalogo : optionsFromRows.classes;
    return source.filter((classe) => classifyClasseGroup(classe) === "REEMPREGO");
  }, [classeCatalogo, optionsFromRows.classes]);

  const resumoNovo = useMemo(() => {
    const map = new Map();
    summaryRows.forEach((row) => {
      if (classifyClasseGroup(row.classe_trilho) !== "NOVO") return;
      const perfil = normalizePerfil(row.perfil_trilho) || "Sem perfil";
      if (!map.has(perfil)) map.set(perfil, { perfil, barras: 0, furos: 0, sem_furos: 0, metros: 0 });
      const entry = map.get(perfil);
      entry.barras += 1;
      entry.metros += toNumber(row.metragem_pecas);
      if (String(row.furos || "").toUpperCase() === "SIM") entry.furos += 1;
      else entry.sem_furos += 1;
    });
    return perfisResumo.map((perfil) => {
      const r = map.get(perfil) || { perfil, barras: 0, furos: 0, sem_furos: 0, metros: 0 };
      return { ...r, toneladas: calcToneladas(r.metros, r.perfil) };
    });
  }, [summaryRows, perfisResumo]);

  const resumoSucata = useMemo(() => {
    const map = new Map();
    summaryRows.forEach((row) => {
      if (classifyClasseGroup(row.classe_trilho) !== "SUCATA") return;
      const perfil = normalizePerfil(row.perfil_trilho) || "Sem perfil";
      if (!map.has(perfil)) map.set(perfil, { perfil, faixa_0_3: 0, faixa_3_7: 0, faixa_7_12: 0, faixa_maior_12: 0, metros: 0 });
      const entry = map.get(perfil);
      const m = toNumber(row.metragem_pecas);
      entry.metros += m;
      if (m >= 0 && m <= 3) entry.faixa_0_3 += m;
      else if (m > 3 && m <= 7) entry.faixa_3_7 += m;
      else if (m > 7 && m <= 12) entry.faixa_7_12 += m;
      else if (m > 12) entry.faixa_maior_12 += m;
    });
    return perfisResumo.map((perfil) => {
      const r = map.get(perfil) || { perfil, faixa_0_3: 0, faixa_3_7: 0, faixa_7_12: 0, faixa_maior_12: 0, metros: 0 };
      return { ...r, toneladas: calcToneladas(r.metros, r.perfil) };
    });
  }, [summaryRows, perfisResumo]);

  const resumoReemprego = useMemo(() => {
    const map = new Map();
    summaryRows.forEach((row) => {
      if (classifyClasseGroup(row.classe_trilho) !== "REEMPREGO") return;
      const perfil = normalizePerfil(row.perfil_trilho) || "Sem perfil";
      const classe = normalizeClasse(row.classe_trilho) || "Sem classe";
      const m = toNumber(row.metragem_pecas);
      if (!map.has(perfil)) map.set(perfil, { perfil, classes: {}, metros: 0 });
      const entry = map.get(perfil);
      entry.classes[classe] = (entry.classes[classe] || 0) + m;
      entry.metros += m;
    });
    return perfisResumo.map((perfil) => {
      const base = map.get(perfil) || { perfil, classes: {}, metros: 0 };
      const classesCount = {};
      classesReempregoResumo.forEach((classe) => {
        classesCount[classe] = base.classes[classe] || 0;
      });
      return { perfil, classes: classesCount, metros: base.metros, toneladas: calcToneladas(base.metros, perfil) };
    });
  }, [summaryRows, perfisResumo, classesReempregoResumo]);

  const onChangeFilter = (event) => {
    const { name, value } = event.target;
    setDraftFilters((prev) => ({ ...prev, [name]: value }));
  };

  const setDraftFilterValue = useCallback((name, value) => {
    setDraftFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  const applyFilters = () => {
    const next = normalizeFilters(draftFilters);
    setDraftFilters(next);
    setActiveFilters(next);
    activeFiltersRef.current = next;
    carregarTudo(next);
  };

  const clearFilters = () => {
    const next = normalizeFilters(DEFAULT_FILTERS);
    setDraftFilters(next);
    setActiveFilters(next);
    activeFiltersRef.current = next;
    carregarTudo(next);
  };

  const loadMore = useCallback(async () => {
    if (isLoading || isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    try {
      await carregarLotes(normalizeFilters(activeFiltersRef.current), { append: true });
    } catch (err) {
      setError(String(err?.message || "Erro ao carregar mais lotes."));
    } finally {
      setIsLoadingMore(false);
    }
  }, [carregarLotes, hasMore, isLoading, isLoadingMore]);

  const onScrollLotes = useCallback(() => {
    const el = lotesScrollRef.current;
    if (!el) return;

    const headEl = lotesHeadScrollRef.current;
    if (headEl && headEl.scrollLeft !== el.scrollLeft) {
      headEl.scrollLeft = el.scrollLeft;
    }

    if (isLoading || isLoadingMore || !hasMore) return;

    const threshold = 160;
    const reachedBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
    if (reachedBottom) loadMore();
  }, [hasMore, isLoading, isLoadingMore, loadMore]);

  const toggleLote = async (codigoLote) => {
    const code = String(codigoLote || "").trim();
    if (!code) return;

    const opening = !expandedLotes[code];
    setExpandedLotes(opening ? { [code]: true } : {});
    if (!opening || loteDetailsByCodigo[code]?.loading || loteDetailsByCodigo[code]?.loaded) return;

    setLoteDetailsByCodigo((prev) => ({
      ...prev,
      [code]: { loading: true, loaded: false, error: "", barras: [] },
    }));

    try {
      const lote = await obterLoteDeposito(code);
      const loteNormalizado = normalizeRealtimeLote(lote);
      const barras = sortSummaryRows(
        (Array.isArray(lote?.barras) ? lote.barras : [])
          .map((barra) => normalizeRealtimeBarra(barra, loteNormalizado))
          .filter(Boolean)
      );
      setLoteDetailsByCodigo((prev) => ({
        ...prev,
        [code]: { loading: false, loaded: true, error: "", barras },
      }));
    } catch (err) {
      setLoteDetailsByCodigo((prev) => ({
        ...prev,
        [code]: { loading: false, loaded: true, error: String(err?.message || "Erro ao carregar barras."), barras: [] },
      }));
    }
  };

  const startEditLoteDocs = (lote) => {
    const code = String(lote?.codigo_lote || "").trim();
    if (!code) return;
    setEditDocsByLote((prev) => ({
      ...prev,
      [code]: {
        notaFiscalText: joinDocumentos(lote?.nota_fiscal),
        pedidoTransferenciaText: joinDocumentos(lote?.pedido_transferencia),
        saving: false,
        error: "",
      },
    }));
  };

  const cancelEditLoteDocs = (codigoLote) => {
    const code = String(codigoLote || "").trim();
    if (!code) return;
    setEditDocsByLote((prev) => {
      const next = { ...prev };
      delete next[code];
      return next;
    });
  };

  const onChangeEditDoc = (codigoLote, field, value) => {
    const code = String(codigoLote || "").trim();
    if (!code) return;
    setEditDocsByLote((prev) => ({
      ...prev,
      [code]: {
        ...prev[code],
        [field]: value,
        error: "",
      },
    }));
  };

  const salvarEditLoteDocs = async (codigoLote) => {
    const code = String(codigoLote || "").trim();
    if (!code) return;
    const draft = editDocsByLote[code];
    if (!draft) return;

    setEditDocsByLote((prev) => ({
      ...prev,
      [code]: {
        ...prev[code],
        saving: true,
        error: "",
      },
    }));

    try {
      const loteAtualizado = await atualizarDocumentosLoteDeposito(code, {
        nota_fiscal: splitDocumentos(draft.notaFiscalText),
        pedido_transferencia: splitDocumentos(draft.pedidoTransferenciaText),
      });
      applyDepositoRealtimePayload({
        action: "UPDATE",
        reason: "documentos",
        codigo_lote: loteAtualizado?.codigo_lote || code,
        lote: loteAtualizado,
        barras: Array.isArray(loteAtualizado?.barras) ? loteAtualizado.barras : [],
        has_barras_snapshot: Array.isArray(loteAtualizado?.barras) && loteAtualizado.barras.length > 0,
      });
      cancelEditLoteDocs(code);
    } catch (err) {
      setEditDocsByLote((prev) => ({
        ...prev,
        [code]: {
          ...prev[code],
          saving: false,
          error: String(err?.message || "Erro ao salvar documentos do lote."),
        },
      }));
    }
  };

  const handleDocsEditorKeyDown = (event, codigoLote) => {
    event.stopPropagation();
    if (event.key === "Escape") {
      event.preventDefault();
      cancelEditLoteDocs(codigoLote);
      return;
    }
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      salvarEditLoteDocs(codigoLote);
    }
  };

  const handleLoteRowClick = (lote) => {
    const code = String(lote?.codigo_lote || "").trim();
    if (!code) return;

    const pending = pendingRowClickRef.current;
    if (pending?.timer && pending.code === code) {
      clearTimeout(pending.timer);
      pendingRowClickRef.current = { timer: null, code: "" };
      const isEditing = Boolean(editDocsByLote[code]);
      if (isEditing) {
        if (!editDocsByLote[code]?.saving) salvarEditLoteDocs(code);
      } else {
        startEditLoteDocs(lote);
      }
      return;
    }

    if (pending?.timer) {
      clearTimeout(pending.timer);
      pendingRowClickRef.current = { timer: null, code: "" };
    }

    const timer = setTimeout(() => {
      pendingRowClickRef.current = { timer: null, code: "" };
      toggleLote(code);
    }, 220);

    pendingRowClickRef.current = { timer, code };
  };

  return (
    <main className="dep-page">
      <div className="dep-root">
        <aside className="dep-sidebar">
          <button type="button" className="dep-back" onClick={onBack}>
            Menu Principal
          </button>
          <h2>Depósito de Trilhos</h2>
          <p>Painel consolidado com lotes e barras registrados no estaleiro.</p>

          <section className="dep-side-card">
            <h3>Filtros</h3>
            <div className="dep-field-row">
              <div className="dep-field">
                <span>Data de</span>
                <DepDateField
                  value={draftFilters.data_de}
                  onChange={(nextValue) => setDraftFilterValue("data_de", nextValue)}
                  ariaLabel="Selecionar data inicial"
                  panelAlign="left"
                />
              </div>
              <div className="dep-field">
                <span>Data até</span>
                <DepDateField
                  value={draftFilters.data_ate}
                  onChange={(nextValue) => setDraftFilterValue("data_ate", nextValue)}
                  ariaLabel="Selecionar data final"
                  panelAlign="right"
                />
              </div>
            </div>
            <div className="dep-field">
              <span>Tipo de lote</span>
              <DepSmoothSelect
                value={draftFilters.tipo_lote}
                onChange={(nextValue) => setDraftFilterValue("tipo_lote", nextValue)}
                options={TIPO_LOTE_FILTER_OPTIONS}
                placeholder="Todos"
                title="Tipo de lote"
              />
            </div>
            <div className="dep-field">
              <span>Perfil</span>
              <DepSearchableSelect
                value={draftFilters.perfil_trilho}
                onChange={(nextValue) => setDraftFilterValue("perfil_trilho", nextValue)}
                options={perfisResumo}
                placeholder="Digite para buscar"
                title="Perfil"
              />
            </div>
            <div className="dep-actions">
              <button type="button" className="dep-btn dep-btn-ghost-dark" onClick={clearFilters} disabled={isLoading}>
                Limpar
              </button>
              <button type="button" className="dep-btn dep-btn-dark" onClick={applyFilters} disabled={isLoading}>
                Aplicar
              </button>
            </div>
            {error ? <p className="dep-error">Erro: {error}</p> : null}
          </section>
        </aside>

        <section className="dep-content">
          <header className="dep-header">
            <div className="dep-header-copy">
              <h1>Depósito de Trilhos</h1>
              <p>Visão em larga escala com lotes e barras do estaleiro.</p>
            </div>
          </header>

          <section className="dep-card dep-card-compact">
            <div className="dep-section-head">
              <h2>Resumo por Perfil e Classe</h2>
            </div>
            <div className="dep-summary-grid">
              <article className="dep-summary-block">
                <h3>Novo</h3>
                <div className="dep-table-wrap dep-table-wrap-sm">
                  <table className="dep-table dep-table-summary">
                    <thead>
                      <tr>
                        <th>Perfil</th><th>Barras</th><th>Furos</th><th>Sem furos</th><th>Metros</th><th>Toneladas</th>
                      </tr>
                    </thead>
                    <tbody>{resumoNovo.map((r) => <tr key={`n-${r.perfil}`}><td className="dep-summary-text">{r.perfil}</td><td>{formatInt(r.barras)}</td><td>{formatInt(r.furos)}</td><td>{formatInt(r.sem_furos)}</td><td>{formatNumber(r.metros)}</td><td>{formatNumber(r.toneladas, 3)}</td></tr>)}</tbody>
                  </table>
                </div>
              </article>

              <article className="dep-summary-block">
                <h3>Reemprego</h3>
                <div className="dep-table-wrap dep-table-wrap-sm">
                  <table className="dep-table dep-table-summary">
                    <thead>
                      <tr>
                        <th>Perfil</th>
                        {classesReempregoResumo.map((c) => (
                          <th key={`c-${c}`}>{c}</th>
                        ))}
                        <th>Metros</th><th>Toneladas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resumoReemprego.map((r) => (
                        <tr key={`r-${r.perfil}`}>
                          <td className="dep-summary-text">{r.perfil}</td>
                          {classesReempregoResumo.map((c) => <td key={`rc-${r.perfil}-${c}`}>{formatNumber(r.classes[c] || 0)}</td>)}
                          <td>{formatNumber(r.metros)}</td>
                          <td>{formatNumber(r.toneladas, 3)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </article>

              <article className="dep-summary-block">
                <h3>Sucata</h3>
                <div className="dep-table-wrap dep-table-wrap-sm">
                  <table className="dep-table dep-table-summary">
                    <thead>
                      <tr>
                        <th>Perfil</th><th>0 a 3 m</th><th>3,01 a 7 m</th><th>7,01 a 12 m</th><th>&gt; 12 m</th><th>Metros</th><th>Toneladas</th>
                      </tr>
                    </thead>
                    <tbody>{resumoSucata.map((r) => <tr key={`s-${r.perfil}`}><td className="dep-summary-text">{r.perfil}</td><td>{formatNumber(r.faixa_0_3)}</td><td>{formatNumber(r.faixa_3_7)}</td><td>{formatNumber(r.faixa_7_12)}</td><td>{formatNumber(r.faixa_maior_12)}</td><td>{formatNumber(r.metros)}</td><td>{formatNumber(r.toneladas, 3)}</td></tr>)}</tbody>
                  </table>
                </div>
              </article>
            </div>
          </section>

          <section className="dep-card dep-card-table">
            <h2>Depósito - Lotes do Estaleiro</h2>
            <div className="dep-table-wrap dep-table-wrap-main">
              <div className="dep-table-head-main" ref={lotesHeadScrollRef}>
                <table className="dep-table dep-table-wide dep-table-main-head">
                  <LoteColGroup />
                  <thead>
                    <tr>
                      {LOTE_COLUMNS.map((column) => (
                        <th key={column.key}>{column.label}</th>
                      ))}
                    </tr>
                  </thead>
                </table>
              </div>

              <div className="dep-table-scroll-main" ref={lotesScrollRef} onScroll={onScrollLotes}>
                <table className="dep-table dep-table-wide dep-table-main-body">
                  <LoteColGroup />
                  <tbody>
                    {visibleLotesRows.map((lote) => {
                      const code = String(lote.codigo_lote || "");
                      return (
                        <DepositoLoteRow
                          key={`row-${code || lote.id}`}
                          lote={lote}
                          isOpen={Boolean(expandedLotes[code])}
                          detail={loteDetailsByCodigo[code]}
                          editState={editDocsByLote[code]}
                          onRowClick={() => handleLoteRowClick(lote)}
                          onNotaFiscalChange={(value) => onChangeEditDoc(code, "notaFiscalText", value)}
                          onPedidoTransferenciaChange={(value) => onChangeEditDoc(code, "pedidoTransferenciaText", value)}
                          onDocsKeyDown={(event) => handleDocsEditorKeyDown(event, code)}
                          onSaveDocs={() => salvarEditLoteDocs(code)}
                          onCancelDocs={() => cancelEditLoteDocs(code)}
                          formatDate={formatDate}
                          formatInt={formatInt}
                          formatNumber={formatNumber}
                          joinDocumentos={joinDocumentos}
                        />
                      );
                    })}

                    {!visibleLotesRows.length ? (
                      <tr>
                        <td colSpan={12} className="dep-empty">
                          {isLoading ? "Carregando dados..." : "Nenhum lote encontrado."}
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
            {isLoadingMore ? <p className="dep-empty">Carregando mais lotes...</p> : null}
          </section>
        </section>
      </div>
    </main>
  );
}






